// @flow
import PropTypes from 'prop-types';
import React, {Component} from 'react';
import {intlShape} from 'react-intl';
import Cleave from 'cleave.js/dist/cleave-react';
import ProgressBar from './ProgressBar/ProgressBar';
import QuoteBox from './QuoteBox/QuoteBox';
import TotalPrice from './TotalPrice/TotalPrice';
import {ProductBox} from './ProductBox/ProductBox';

import style from './NewProductForm.scss';

import type {InputQuantityAction} from '../../../../../elements/InputQuantity/InputQuantity';
import type {Option} from '../../../../../elements/Select/Select';
import type {Category, ScrappedDetailOptionItem, ScrappedProduct} from '../../../../../entities';
import type {Location as RouterLocation} from '../../../../../router';
import type {LocalValuesState} from '../../../../local-values/duck';
import type {LocaleState} from '../../../../locale/duck';
import type {Forecast, ForecastCourier, ForecastPricesApiReq, ForecastTier} from '../../../QuoteStep/api';
import type {ForecastType} from './QuoteBox/ForecastBox/ForecastBox';

export type Props = {
  +handleChangeMethod: ({ type: ForecastType; subtype: string }) => void;
  +quoteMethodType?: ForecastType;
  +quoteFullSubtype?: string;
  +quoteLiteSubtype?: string;
  +forecast?: Forecast;
  +isLoadingForecast?: boolean;
  +forecastRequest?: ForecastPricesApiReq;
  +handleChangeInputText: (e: Event, value?: string) => void;
  +handleChangeInputUrl: (e: Event) => void;
  +handleOnKeyPress: (e: KeyboardEvent) => void;
  +handleFocusInputText: (e: Event) => void;
  +handleBlurCleave: (e: Event) => void;
  +handleDeleteImage: () => void;
  +handleChangeImage: (acceptedFiles: File[]) => void;
  +handleWeightInit: (cleave: Cleave) => void;
  +handlePriceInit: (cleave: Cleave) => void;
  +handleShippingInit: (cleave: Cleave) => void;
  +handleChangeCleave: (e: Event) => void;
  +handleChangeCategory: (option: Option) => void;
  +handleChangeAmount: (action: InputQuantityAction) => void;
  +handleChangeWeightUnit: (index: string) => void;
  +handleChangeVolume: (value: number) => void;
  +handleChangeBoxOption: (index: number) => void;
  +handleClickAddCart: () => void;
  +handleClickBuyNow: () => void;
  +handleOpenShippingHelpModal: () => void;
  +categories?: Category[];
  +locale: LocaleState;
  +location: RouterLocation;
  +localValues: LocalValuesState;
  +departureCountry: {
    value: string;
    errors?: string[];
  };
  +url: {
    value: string;
    errors?: string[];
  };
  +name: {
    value: string;
    errors?: string[];
  };
  +image: {
    value?: string;
    valueURI?: string;
    errors?: string[];
  };
  +description: {
    value: string;
    errors?: string[];
  };
  +shipping: string;
  +price: {
    value: string;
    errors?: string[];
  };
  +categoryId: {
    value: string;
    errors?: string[];
  };
  +amount: {
    value: number;
    errors?: string[];
  };
  +weightUnit: {
    value: string;
  };
  +weight: {
    value: string;
    errors?: string[];
  };
  +volumeTier: {
    value: ?number;
    code: string;
    errors?: string[];
  };
  +withBox: {
    value: number;
    errors?: string[];
  };
  +isScrapping: boolean;
  +hideOptions: boolean;
  // +chosenVariants?: { [key: string]: number };
  +scrappedProduct?: ScrappedProduct;
  +handleChangeDetailOption: (dimension: string, value: string) => void;
  +getVariantFromStateDetailOption: () => ?ScrappedDetailOptionItem;
  +detailOptions: {
    [key: string]: {
      value: string;
      errors?: string[];
      disabled?: {
        [key: string]: boolean;
      };
    };
  };
  +vendorData: { [key: string]: any };
  +preferredImportationMethod?: string;
};

export default class NewProductForm extends Component<Props> {
  getCompleteness = (): number => {
    const {
      url, name, image, categoryId, volumeTier, weight, price, localValues,
    } = this.props;
    const values = [url.value, name.value, image.value || image.valueURI, categoryId.value, price.value];

    // Has volumeTier
    if (
      !!localValues.categoriesMap && !!categoryId.value &&
      !!localValues.categoriesMap[`${categoryId.value}`] &&
      localValues.categoriesMap[`${categoryId.value}`].volumeTiers.length !== 0
    ) {
      values.push(volumeTier.value);
    }

    // Has weight
    if (
      !!localValues.categoriesMap && !!categoryId.value &&
      localValues.categoriesMap[`${categoryId.value}`].criteria === 'WEIGHT'
    ) {
      values.push(weight.value);
    }

    let completed = 0;

    for (let val of values) {
      if (val !== undefined && val !== '') completed += 1;
    }
    return Math.floor((completed / values.length) * 100);
  };

  render() {
    const {
      handleChangeMethod, forecast, isLoadingForecast, forecastRequest, quoteMethodType,
      quoteFullSubtype, quoteLiteSubtype,
      volumeTier,
      handleClickAddCart, handleClickBuyNow, preferredImportationMethod,
    } = this.props;

    const percent = this.getCompleteness();

    const selectedForecastClassic: ForecastTier | typeof undefined = forecast && quoteMethodType === 'FULL' && quoteFullSubtype ?
      forecast['CLASSIC'].find((f: ForecastTier) => f.tier.name === quoteFullSubtype) : undefined;

    const selectedForecastCourier: ForecastCourier | typeof undefined = forecast && quoteMethodType === 'LITE' && quoteLiteSubtype ?
      forecast['DIY'].find((f: ForecastCourier) => f.courier.id === quoteLiteSubtype) : undefined;

    // const forecastCourier = quoteMethodType === 'LITE' && quoteSubMethod ? 

    return (
      <div className={style.newProductFormWrap}>
        <div className={style.headerWrap}>
          {percent !== 100 && <h2 className={style.title}>Termina de completar los datos</h2>}
          {percent === 100 && <h2 className={style.title}>¡Completaste todo!</h2>}
          <ProgressBar percent={percent}/>
        </div>
        <div className={style.contentWrap}>
          <div className={style.leftWrap}>
            <ProductBox {...this.props} />
            <br/>
            <QuoteBox handleChangeMethod={handleChangeMethod}
                      quoteMethodType={quoteMethodType}
                      quoteFullSubtype={quoteFullSubtype}
                      quoteLiteSubtype={quoteLiteSubtype}
                      forecast={forecast}
                      isLoadingForecast={isLoadingForecast}
                      forecastRequest={forecastRequest}
                      preferredImportationMethod={preferredImportationMethod}
            />
          </div>
          <div className={style.rightWrap}>
            <div className={style.sticky}>
              <TotalPrice
                forecastClassic={selectedForecastClassic}
                forecastCourier={selectedForecastCourier}
                handleClickAddCart={handleClickAddCart}
                handleClickBuyNow={handleClickBuyNow}
                forecastRequest={forecastRequest}
              />
            </div>
          </div>
        </div>
      </div>
    );
  }
}

NewProductForm.propTypes = {
  handleChangeMethod: PropTypes.func.isRequired,
  quoteMethodType: PropTypes.string,
  quoteFullSubtype: PropTypes.string,
  quoteLiteSubtype: PropTypes.string,
  forecast: PropTypes.object,
  isLoadingForecast: PropTypes.bool,
  forecastRequest: PropTypes.object,
  handleChangeInputText: PropTypes.func.isRequired,
  handleChangeInputUrl: PropTypes.func.isRequired,
  handleOnKeyPress: PropTypes.func.isRequired,
  handleFocusInputText: PropTypes.func.isRequired,
  handleBlurCleave: PropTypes.func.isRequired,
  handleDeleteImage: PropTypes.func.isRequired,
  handleChangeImage: PropTypes.func.isRequired,
  handleWeightInit: PropTypes.func.isRequired,
  handlePriceInit: PropTypes.func.isRequired,
  handleShippingInit: PropTypes.func.isRequired,
  handleChangeCleave: PropTypes.func.isRequired,
  handleChangeCategory: PropTypes.func.isRequired,
  handleChangeAmount: PropTypes.func.isRequired,
  handleChangeWeightUnit: PropTypes.func.isRequired,
  handleChangeVolume: PropTypes.func.isRequired,
  handleChangeBoxOption: PropTypes.func.isRequired,
  handleClickAddCart: PropTypes.func.isRequired,
  handleClickBuyNow: PropTypes.func.isRequired,
  handleOpenShippingHelpModal: PropTypes.func.isRequired,
  categories: PropTypes.array,
  locale: PropTypes.object.isRequired,
  location: PropTypes.object.isRequired,
  localValues: PropTypes.object,
  departureCountry: PropTypes.object.isRequired,
  url: PropTypes.object.isRequired,
  name: PropTypes.object.isRequired,
  image: PropTypes.object.isRequired,
  description: PropTypes.object.isRequired,
  shipping: PropTypes.string.isRequired,
  price: PropTypes.object.isRequired,
  categoryId: PropTypes.object.isRequired,
  amount: PropTypes.object.isRequired,
  weightUnit: PropTypes.object.isRequired,
  weight: PropTypes.object.isRequired,
  volumeTier: PropTypes.object.isRequired,
  withBox: PropTypes.object.isRequired,
  isScrapping: PropTypes.bool.isRequired,
  hideOptions: PropTypes.bool.isRequired,
  // chosenVariants: PropTypes.object,
  scrappedProduct: PropTypes.object,
  handleChangeDetailOption: PropTypes.func.isRequired,
  getVariantFromStateDetailOption: PropTypes.func.isRequired,
  detailOptions: PropTypes.object,
  vendorData: PropTypes.object,
  preferredImportationMethod: PropTypes.string,
};

NewProductForm.contextTypes = {
  intl: intlShape,
};



// WEBPACK FOOTER //
// ./src/modules/new-order/ProductStep/components/NewProductForm/NewProductForm.jsx